import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { AppointmentType } from '../../../../contexts/PsychologistContext';
import { createPsychologistWorkSchedule } from '../../../../hooks/api/schedule/create';
import {
  updatePsychologistWorkSchedule,
  deletePsychologistWorkSchedule,
} from '../../../../hooks/api/schedule/update';
import { updateOneTimeSlots } from '../../../../hooks/api/schedule/oneTimeSlots';
import { deleteBlockage } from '../../../../hooks/api/blockage';
import { timeToMinutes } from '../utils';
import { findAdjacentSchedules, convertFrontendDayToBackend } from '../validation';
import {
  localTimeToUTC,
  localTimeToUTCWithDayShift,
} from '../workScheduleTimeHelpers';
import { invalidateSchedulesCache, invalidateSessionsCache } from '../data';
import type { WorkSchedule, SessionWithPsychologist } from './useCalendarData';
import type { PreviewSchedule } from '../WorkSchedulePanel';
import type { ScheduleAction } from '../WorkSchedulePanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DateSpecificSlot = {
  date: string;
  startTime: string;
  endTime: string;
  scheduleType?: 'standard' | 'reschedule_only';
};

export interface UseCalendarScheduleOpsOptions {
  psychologist: { id: string | number };
  psychologistId: string | null | undefined;
  appointmentTypes: AppointmentType[];
  template: WorkSchedule[];
  setTemplate: React.Dispatch<React.SetStateAction<WorkSchedule[]>>;
  setTemplateReady: React.Dispatch<React.SetStateAction<boolean>>;
  startOfWeek: Date;
  selectedTimeZone: string;
  editMode: boolean;
  openCreateModalForDate?: string | null;
  onCloseCreateModal?: () => void;
  onSpecificDayHasChangesChange?: (v: boolean) => void;
  onBlockHasChangesChange?: (v: boolean) => void;
  onCloseBlockModal: () => void;
  onScheduleHasChangesChange?: (hasChanges: boolean) => void;
  panelPreviewSchedules: PreviewSchedule[] | null;
  setPanelPreviewSchedules: React.Dispatch<React.SetStateAction<PreviewSchedule[] | null>>;
  setDeletedScheduleIds: React.Dispatch<React.SetStateAction<Set<string | number>>>;
  deletedScheduleIds: Set<string | number>;
  dateSpecificSlots: DateSpecificSlot[];
  setDateSpecificSlots: React.Dispatch<React.SetStateAction<DateSpecificSlot[]>>;
  sessionsCacheByWeekRef: React.MutableRefObject<Map<string, SessionWithPsychologist[]>>;
  pendingFetchesRef: React.MutableRefObject<Map<string, Promise<unknown>>>;
  availabilityCacheRef: React.MutableRefObject<Map<string, SessionWithPsychologist[]>>;
  dateSpecificSlotsCacheRef: React.MutableRefObject<
    Map<string, DateSpecificSlot[]>
  >;
  availabilityPendingRef: React.MutableRefObject<
    Map<string, Promise<SessionWithPsychologist[]>>
  >;
  schedulesCacheRef: React.MutableRefObject<WorkSchedule[] | undefined>;
  schedulesCachePsychIdRef: React.MutableRefObject<string | undefined>;
  sessions: SessionWithPsychologist[];
  setSessions: React.Dispatch<React.SetStateAction<SessionWithPsychologist[]>>;
  loadData: () => Promise<void>;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

export interface UseCalendarScheduleOpsReturn {
  // State
  templateModal: {
    date: string;
    startTime: string;
    endTime: string;
    weekdayIdx: number;
  } | null;
  setTemplateModal: React.Dispatch<
    React.SetStateAction<{
      date: string;
      startTime: string;
      endTime: string;
      weekdayIdx: number;
    } | null>
  >;
  editModal: WorkSchedule | null;
  setEditModal: React.Dispatch<React.SetStateAction<WorkSchedule | null>>;
  editSpecificDayModal: {
    date: string;
    startTime: string;
    endTime: string;
    scheduleType?: 'standard' | 'reschedule_only';
  } | null;
  setEditSpecificDayModal: React.Dispatch<
    React.SetStateAction<{
      date: string;
      startTime: string;
      endTime: string;
      scheduleType?: 'standard' | 'reschedule_only';
    } | null>
  >;
  specificDayPreview: DateSpecificSlot[];
  setSpecificDayPreview: React.Dispatch<React.SetStateAction<DateSpecificSlot[]>>;
  specificDaySelectedDate: string | null;
  setSpecificDaySelectedDate: React.Dispatch<React.SetStateAction<string | null>>;
  showLoadingModal: boolean;
  setShowLoadingModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRangeId: string | number | null;
  setSelectedRangeId: React.Dispatch<React.SetStateAction<string | number | null>>;
  // Refs
  workScheduleSavedRef: React.MutableRefObject<boolean>;
  specificDayHasChangesRef: React.MutableRefObject<boolean>;
  blockHasChangesRef: React.MutableRefObject<boolean>;
  // Computed
  openCreateWeekdayIdx: number;
  // Handlers
  handleSetTemplateModal: (
    modal: {
      date: string;
      startTime: string;
      endTime: string;
      weekdayIdx: number;
    } | null
  ) => void;
  handlePanelSave: (actions: ScheduleAction[]) => Promise<void>;
  handleSpecificDaySave: (
    inputs: Array<{
      startTime: string;
      endTime: string;
      weekdayIdx: number;
      date: string;
      scheduleType: 'standard' | 'reschedule_only';
    }>,
    date: string
  ) => Promise<void>;
  handleCreateWorkRangeModalSave: (
    inputs: Array<{
      startTime: string;
      endTime: string;
      weekdayIdx?: number;
      scheduleType?: 'standard' | 'reschedule_only';
    }>
  ) => Promise<void>;
  handleEditWorkRangeModalDelete: (scheduleId: string | number) => Promise<void>;
  handleEditWorkRangeModalSave: (input: {
    scheduleId: string | number;
    weekdayIdx: number;
    startTime: string;
    endTime: string;
    scheduleType?: 'standard' | 'reschedule_only';
  }) => Promise<void>;
  handleEditSpecificDayModalDelete: () => Promise<void>;
  handleEditSpecificDayModalSave: (input: {
    scheduleId: string | number;
    weekdayIdx: number;
    startTime: string;
    endTime: string;
    scheduleType?: 'standard' | 'reschedule_only';
  }) => Promise<void>;
  handleWorkRangeOverlayDelete: (scheduleId: string | number) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useCalendarScheduleOps({
  psychologist,
  appointmentTypes,
  template,
  setTemplate,
  setTemplateReady,
  startOfWeek,
  selectedTimeZone,
  editMode,
  openCreateModalForDate,
  onSpecificDayHasChangesChange,
  onBlockHasChangesChange,
  dateSpecificSlots,
  sessionsCacheByWeekRef,
  pendingFetchesRef,
  availabilityCacheRef,
  dateSpecificSlotsCacheRef,
  availabilityPendingRef,
  schedulesCacheRef,
  schedulesCachePsychIdRef,
  sessions,
  setSessions,
  loadData,
  showSuccess,
  showError,
}: UseCalendarScheduleOpsOptions): UseCalendarScheduleOpsReturn {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const [templateModal, setTemplateModal] = useState<{
    date: string;
    startTime: string;
    endTime: string;
    weekdayIdx: number;
  } | null>(null);

  const [editModal, setEditModal] = useState<WorkSchedule | null>(null);

  const [editSpecificDayModal, setEditSpecificDayModal] = useState<{
    date: string;
    startTime: string;
    endTime: string;
    scheduleType?: 'standard' | 'reschedule_only';
  } | null>(null);

  const [specificDayPreview, setSpecificDayPreview] = useState<DateSpecificSlot[]>([]);

  const [specificDaySelectedDate, setSpecificDaySelectedDate] = useState<string | null>(null);

  const [showLoadingModal, setShowLoadingModal] = useState<boolean>(false);

  const [selectedRangeId, setSelectedRangeId] = useState<string | number | null>(null);

  // -------------------------------------------------------------------------
  // Refs
  // -------------------------------------------------------------------------

  // Tracks whether WorkSchedulePanel just finished a successful save — used to
  // suppress the "unsaved changes" modal in CalendarTab when onCancel fires
  // synchronously right after the save (stale closure issue).
  const workScheduleSavedRef = useRef(false);
  const specificDayHasChangesRef = useRef(false);
  const blockHasChangesRef = useRef(false);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Sync specificDaySelectedDate when SpecificDayPanel opens or closes
  useEffect(() => {
    setSpecificDaySelectedDate(openCreateModalForDate ?? null);
  }, [openCreateModalForDate]);

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  // Weekday index (0 = Mon … 6 = Sun) of the date being edited in the SpecificDayPanel.
  // Returns -1 when no panel is open.
  const openCreateWeekdayIdx = useMemo(() => {
    if (!openCreateModalForDate) return -1;
    const [y, m, d] = openCreateModalForDate.split('-').map(Number);
    if (!y) return -1;
    const jsDay = new Date(y, (m || 1) - 1, d || 1).getDay();
    return jsDay === 0 ? 6 : jsDay - 1; // Mon=0 … Sun=6
  }, [openCreateModalForDate]);

  // -------------------------------------------------------------------------
  // Private helper: invalidate all relevant caches after a schedule change
  // -------------------------------------------------------------------------

  const invalidateAllCaches = useCallback(() => {
    const psychId = String(psychologist.id);
    invalidateSchedulesCache(psychId);
    schedulesCacheRef.current = undefined;
    schedulesCachePsychIdRef.current = undefined;

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeek.toISOString().slice(0, 10)}_${psychologist.id}`;
    sessionsCacheByWeekRef.current.delete(weekKey);
    pendingFetchesRef.current.delete(`sessions:${weekKey}`);
    invalidateSessionsCache(weekKey);
    availabilityCacheRef.current.delete(weekKey);
    dateSpecificSlotsCacheRef.current.delete(weekKey);
    availabilityPendingRef.current.delete(weekKey);
  }, [
    psychologist.id,
    startOfWeek,
    schedulesCacheRef,
    schedulesCachePsychIdRef,
    sessionsCacheByWeekRef,
    pendingFetchesRef,
    availabilityCacheRef,
    dateSpecificSlotsCacheRef,
    availabilityPendingRef,
  ]);

  // -------------------------------------------------------------------------
  // handleSetTemplateModal
  // -------------------------------------------------------------------------

  // Wrapper for setTemplateModal that dispatches event in editMode instead of
  // opening the modal directly (WorkSchedulePanel handles drag-created ranges).
  const handleSetTemplateModal = useCallback(
    (
      modal: {
        date: string;
        startTime: string;
        endTime: string;
        weekdayIdx: number;
      } | null
    ) => {
      if (modal && editMode) {
        window.dispatchEvent(
          new CustomEvent('psi-add-work-range-from-drag', {
            detail: {
              weekdayIdx: modal.weekdayIdx,
              startTime: modal.startTime,
              endTime: modal.endTime,
            },
          })
        );
      } else {
        setTemplateModal(modal);
      }
    },
    [editMode]
  );

  // -------------------------------------------------------------------------
  // handlePanelSave — CRUD for WorkSchedulePanel (recurring schedules)
  // -------------------------------------------------------------------------

  const handlePanelSave = useCallback(
    async (actions: ScheduleAction[]) => {
      setShowLoadingModal(true);
      try {
        const psychId = String(psychologist.id);
        const appointmentTypeIds = appointmentTypes.map((apt) => apt.id);

        // 1. Deletes first
        const deletes = actions.filter(
          (a): a is Extract<ScheduleAction, { type: 'delete' }> => a.type === 'delete'
        );
        for (const action of deletes) {
          await deletePsychologistWorkSchedule(String(action.scheduleId));
        }

        // 2. Updates (with adjacency merge logic)
        const updates = actions.filter(
          (a): a is Extract<ScheduleAction, { type: 'update' }> => a.type === 'update'
        );
        let localTemplateForUpdates = [...template];
        // Remove deleted items so adjacency checks don't see them
        const deletedIdSet = new Set(deletes.map((a) => String(a.scheduleId)));
        localTemplateForUpdates = localTemplateForUpdates.filter(
          (t) => !deletedIdSet.has(String(t.id))
        );

        for (const action of updates) {
          const inputStart = action.startTime.slice(0, 5);
          const inputEnd = action.endTime.slice(0, 5);
          const startTimeWithSeconds =
            action.startTime.length === 5 ? `${action.startTime}:00` : action.startTime;
          const endTimeWithSeconds =
            action.endTime.length === 5 ? `${action.endTime}:00` : action.endTime;
          const startUTCResult = localTimeToUTCWithDayShift(startTimeWithSeconds, selectedTimeZone);
          const startTimeUTC = startUTCResult.time;
          const endTimeUTC = localTimeToUTC(endTimeWithSeconds, selectedTimeZone);
          const adjustedWeekdayIdx = ((action.weekdayIdx + startUTCResult.dayShift) % 7 + 7) % 7;
          const backendWeekday = convertFrontendDayToBackend(adjustedWeekdayIdx);

          // Apply the basic update first
          await updatePsychologistWorkSchedule(String(action.scheduleId), {
            weekday: backendWeekday,
            start_time: startTimeUTC,
            end_time: endTimeUTC,
            active: true,
            schedule_type: action.scheduleType,
          });

          // Update local template with new values
          localTemplateForUpdates = localTemplateForUpdates.map((t) =>
            String(t.id) === String(action.scheduleId)
              ? {
                  ...t,
                  weekday: action.weekdayIdx,
                  startTime: inputStart,
                  endTime: inputEnd,
                  active: true,
                  scheduleType: action.scheduleType,
                }
              : t
          );

          // Check adjacency and merge if needed
          const othersOnly = localTemplateForUpdates.filter(
            (t) => String(t.id) !== String(action.scheduleId)
          );
          const { above, below } = findAdjacentSchedules(
            othersOnly.map((t) => ({ ...t, id: String(t.id) })),
            action.weekdayIdx,
            inputStart,
            inputEnd,
            action.scheduleType
          );

          if (above && below) {
            const belowEndWithSeconds =
              below.endTime.length === 5 ? `${below.endTime}:00` : below.endTime;
            const belowEndUTC = localTimeToUTC(belowEndWithSeconds, selectedTimeZone);
            const aboveStartWithSeconds =
              above.startTime.length === 5 ? `${above.startTime}:00` : above.startTime;
            const aboveStartUTC = localTimeToUTC(aboveStartWithSeconds, selectedTimeZone);
            await updatePsychologistWorkSchedule(String(above.id), {
              start_time: aboveStartUTC,
              end_time: belowEndUTC,
              skip_slot_management: true,
            });
            await deletePsychologistWorkSchedule(String(action.scheduleId), {
              preserve_slots: true,
            });
            await deletePsychologistWorkSchedule(String(below.id), { preserve_slots: true });
            localTemplateForUpdates = localTemplateForUpdates
              .map((t) =>
                String(t.id) === String(above.id) ? { ...t, endTime: below.endTime } : t
              )
              .filter(
                (t) =>
                  String(t.id) !== String(action.scheduleId) &&
                  String(t.id) !== String(below.id)
              );
          } else if (above) {
            await updatePsychologistWorkSchedule(String(above.id), {
              end_time: endTimeUTC,
              skip_slot_management: true,
            });
            await deletePsychologistWorkSchedule(String(action.scheduleId), {
              preserve_slots: true,
            });
            localTemplateForUpdates = localTemplateForUpdates
              .map((t) =>
                String(t.id) === String(above.id) ? { ...t, endTime: inputEnd } : t
              )
              .filter((t) => String(t.id) !== String(action.scheduleId));
          } else if (below) {
            await updatePsychologistWorkSchedule(String(below.id), {
              start_time: startTimeUTC,
              skip_slot_management: true,
            });
            await deletePsychologistWorkSchedule(String(action.scheduleId), {
              preserve_slots: true,
            });
            localTemplateForUpdates = localTemplateForUpdates
              .map((t) =>
                String(t.id) === String(below.id) ? { ...t, startTime: inputStart } : t
              )
              .filter((t) => String(t.id) !== String(action.scheduleId));
          }
        }

        // 3. Creates (with adjacency merge logic)
        const creates = actions.filter((a) => a.type === 'create');
        let localTemplate = [...localTemplateForUpdates];
        for (const action of creates) {
          if (action.type !== 'create') continue;
          const inputStart = action.startTime.slice(0, 5);
          const inputEnd = action.endTime.slice(0, 5);
          const startTimeWithSeconds =
            action.startTime.length === 5 ? `${action.startTime}:00` : action.startTime;
          const endTimeWithSeconds =
            action.endTime.length === 5 ? `${action.endTime}:00` : action.endTime;
          const startUTCResult = localTimeToUTCWithDayShift(startTimeWithSeconds, selectedTimeZone);
          const startTimeUTC = startUTCResult.time;
          const endTimeUTC = localTimeToUTC(endTimeWithSeconds, selectedTimeZone);
          const adjustedWeekdayIdx = ((action.weekdayIdx + startUTCResult.dayShift) % 7 + 7) % 7;
          const backendWeekday = convertFrontendDayToBackend(adjustedWeekdayIdx);

          const { above, below } = findAdjacentSchedules(
            localTemplate.map((t) => ({ ...t, id: String(t.id) })),
            action.weekdayIdx,
            inputStart,
            inputEnd,
            action.scheduleType
          );

          if (above && below) {
            await updatePsychologistWorkSchedule(String(above.id), { end_time: endTimeUTC });
            const belowEndWithSeconds =
              below.endTime.length === 5 ? `${below.endTime}:00` : below.endTime;
            const belowEndUTC = localTimeToUTC(belowEndWithSeconds, selectedTimeZone);
            await updatePsychologistWorkSchedule(String(above.id), {
              end_time: belowEndUTC,
              skip_slot_management: true,
            });
            await deletePsychologistWorkSchedule(String(below.id), { preserve_slots: true });
            localTemplate = localTemplate
              .map((t) =>
                String(t.id) === String(above.id) ? { ...t, endTime: below.endTime } : t
              )
              .filter((t) => String(t.id) !== String(below.id));
          } else if (above) {
            await updatePsychologistWorkSchedule(String(above.id), { end_time: endTimeUTC });
            localTemplate = localTemplate.map((t) =>
              String(t.id) === String(above.id) ? { ...t, endTime: inputEnd } : t
            );
          } else if (below) {
            await updatePsychologistWorkSchedule(String(below.id), { start_time: startTimeUTC });
            localTemplate = localTemplate.map((t) =>
              String(t.id) === String(below.id) ? { ...t, startTime: inputStart } : t
            );
          } else {
            const payload = {
              weekday: backendWeekday,
              start_time: startTimeUTC,
              end_time: endTimeUTC,
              active: true,
              appointment_types: appointmentTypeIds.length > 0 ? appointmentTypeIds : undefined,
              schedule_type: action.scheduleType,
            };
            const created = await createPsychologistWorkSchedule(payload);
            const createdId = (created as unknown as { id?: string | number } | null)?.id;
            localTemplate.push({
              id:
                typeof createdId === 'string' || typeof createdId === 'number'
                  ? createdId
                  : `${psychId}-${action.weekdayIdx}-${inputStart}`,
              psychologistId: psychId,
              weekday: action.weekdayIdx,
              startTime: inputStart,
              endTime: inputEnd,
              active: true,
              scheduleType: action.scheduleType ?? 'standard',
            });
          }
        }

        setTemplate(localTemplate);
        setTemplateReady(true);

        // Caches invalidated here — backend handles cleanup of overlapping one_time_availability
        // slots when creating/updating work schedules (cleanupOverlappingOneTimeSlots).
        invalidateAllCaches();
        await loadData();

        const totalChanges = actions.length;
        showSuccess(
          totalChanges === 1
            ? 'Horario actualizado exitosamente'
            : `${totalChanges} cambios guardados exitosamente`
        );
        // Notifica a AppShell para que re-evalúe el redirect a quierosumarme (psicóloga NZ
        // sin procesos que recién cargó horarios) sin esperar a un reload de la pantalla.
        window.dispatchEvent(new CustomEvent('psi-work-schedule-saved'));
      } catch (error) {
        console.error('Error saving panel schedules:', error);
        showError('Error al guardar los cambios del horario');
      } finally {
        setShowLoadingModal(false);
      }
    },
    [
      psychologist.id,
      appointmentTypes,
      template,
      selectedTimeZone,
      loadData,
      showSuccess,
      showError,
      setTemplate,
      setTemplateReady,
      invalidateAllCaches,
    ]
  );

  // -------------------------------------------------------------------------
  // handleSpecificDaySave — save one-time slots for a specific date
  // -------------------------------------------------------------------------

  const handleSpecificDaySave = useCallback(
    async (
      inputs: Array<{
        startTime: string;
        endTime: string;
        weekdayIdx: number;
        date: string;
        scheduleType: 'standard' | 'reschedule_only';
      }>,
      date: string
    ) => {
      setShowLoadingModal(true);
      try {
        const dateStr = date || (inputs.length > 0 ? inputs[0].date : openCreateModalForDate);
        if (!dateStr) throw new Error('No date available');
        const [y, m, d] = dateStr.split('-').map(Number);
        void y; void m; void d; // accessed implicitly below via shiftDate

        // Delete only the blocked slots that overlap with the new ranges
        if (inputs.length > 0) {
          const blockedForDate = sessions.filter(s => s.status === 'blocked' && s.date === dateStr);
          for (const blocked of blockedForDate) {
            const bStart = timeToMinutes(blocked.time);
            const bEnd = bStart + (blocked.duration || 30);
            for (const input of inputs) {
              const rStart = timeToMinutes(input.startTime);
              let rEnd = timeToMinutes(input.endTime);
              if (rEnd === 0) rEnd = 1440;
              const overlapStart = Math.max(bStart, rStart);
              const overlapEnd = Math.min(bEnd, rEnd);
              if (overlapStart < overlapEnd && blocked.origISO) {
                const blockStartUTC = new Date(blocked.origISO);
                const overlapFromUTC = new Date(blockStartUTC.getTime() + (overlapStart - bStart) * 60000);
                const overlapToUTC = new Date(blockStartUTC.getTime() + (overlapEnd - bStart) * 60000);
                const utcDate = overlapFromUTC.toISOString().split('T')[0];
                const utcStart = overlapFromUTC.toISOString().split('T')[1].substring(0, 5);
                const utcEnd = overlapToUTC.toISOString().split('T')[1].substring(0, 5);
                try {
                  await deleteBlockage({ date: utcDate, start_time: utcStart, end_time: utcEnd });
                } catch (error) {
                  console.error('Error deleting overlapping blockage:', error);
                }
              }
            }
          }
        }

        // Helper: shift a YYYY-MM-DD date by N days
        const shiftDate = (ds: string, days: number): string => {
          const [sy, sm, sd] = ds.split('-').map(Number);
          const shifted = new Date(sy, sm - 1, sd + days);
          const sy2 = shifted.getFullYear();
          const sm2 = String(shifted.getMonth() + 1).padStart(2, '0');
          const sd2 = String(shifted.getDate()).padStart(2, '0');
          return `${sy2}-${sm2}-${sd2}`;
        };

        // Convert start time to UTC using the same timezone helpers as recurring schedules.
        // dayShift tells us if the resulting UTC date differs from the local date.
        const sampleStartResult = localTimeToUTCWithDayShift(
          inputs.length > 0 ? `${inputs[0].startTime}:00` : '12:00:00',
          selectedTimeZone
        );
        const utcDate = shiftDate(dateStr, sampleStartResult.dayShift);

        if (inputs.length === 0) {
          // Delete all one_time_availability slots for this date (both types).
          // Clear the base UTC date and its neighbours to cover all possible slots.
          const utcDateBase = shiftDate(dateStr, 0);
          const datesToClear = new Set([
            utcDate,
            utcDateBase,
            shiftDate(dateStr, 1),
            shiftDate(dateStr, -1),
          ]);
          for (const ud of Array.from(datesToClear)) {
            await updateOneTimeSlots({ date: ud, ranges: [], schedule_type: 'standard' });
            await updateOneTimeSlots({ date: ud, ranges: [], schedule_type: 'reschedule_only' });
          }
        } else {
          // Group inputs by scheduleType so we can make separate API calls per type
          const byType: Record<string, typeof inputs> = {};
          for (const input of inputs) {
            const st = input.scheduleType ?? 'standard';
            if (!byType[st]) byType[st] = [];
            byType[st].push(input);
          }

          // Types that had slots before but no longer have any must be cleared.
          // Clear on multiple UTC dates because different local times may map to different UTC dates.
          const activeTypes = new Set(Object.keys(byType));
          const datesToClear = new Set([utcDate, shiftDate(dateStr, 0), shiftDate(dateStr, 1), shiftDate(dateStr, -1)]);
          for (const type of ['standard', 'reschedule_only'] as const) {
            if (!activeTypes.has(type)) {
              for (const ud of Array.from(datesToClear)) {
                await updateOneTimeSlots({ date: ud, ranges: [], schedule_type: type });
              }
            }
          }

          for (const [scheduleType, group] of Object.entries(byType)) {
            // Convert each range to UTC using the same timezone helpers as recurring schedules,
            // and group by the resulting UTC date (dayShift may differ per range).
            const rangesByUtcDate: Record<string, Array<{ start_time: string; end_time: string }>> =
              {};
            for (const input of group) {
              const startWithSec =
                input.startTime.length === 5 ? `${input.startTime}:00` : input.startTime;
              const endWithSec =
                input.endTime.length === 5 ? `${input.endTime}:00` : input.endTime;
              const startUTCResult = localTimeToUTCWithDayShift(startWithSec, selectedTimeZone);
              const endTimeUTC = localTimeToUTC(endWithSec, selectedTimeZone);
              const rangeUtcDate = shiftDate(dateStr, startUTCResult.dayShift);
              if (!rangesByUtcDate[rangeUtcDate]) rangesByUtcDate[rangeUtcDate] = [];
              rangesByUtcDate[rangeUtcDate].push({
                start_time: startUTCResult.time.slice(0, 5),
                end_time: endTimeUTC.slice(0, 5),
              });
            }
            for (const [rangeUtcDate, ranges] of Object.entries(rangesByUtcDate)) {
              await updateOneTimeSlots({
                date: rangeUtcDate,
                ranges,
                schedule_type: scheduleType as 'standard' | 'reschedule_only',
              });
            }
          }
        }

        invalidateAllCaches();

        // Clear blocked sessions from state so they don't survive the merge in loadData
        setSessions(prev => prev.filter(s => s.status !== 'blocked'));

        await loadData();

        showSuccess(
          inputs.length === 0
            ? 'Horarios eliminados exitosamente'
            : inputs.length === 1
            ? 'Horario guardado exitosamente'
            : `${inputs.length} horarios guardados exitosamente`
        );
      } catch (error) {
        console.error('Error creating one-time slots:', error);
        showError('Error al crear los horarios');
      } finally {
        setShowLoadingModal(false);
      }
    },
    [
      selectedTimeZone,
      loadData,
      showSuccess,
      showError,
      openCreateModalForDate,
      invalidateAllCaches,
      sessions,
      setSessions,
    ]
  );

  // -------------------------------------------------------------------------
  // handleCreateWorkRangeModalSave — onSave handler for CreateWorkRangeModal
  // -------------------------------------------------------------------------

  const handleCreateWorkRangeModalSave = useCallback(
    async (
      inputs: Array<{
        startTime: string;
        endTime: string;
        weekdayIdx?: number;
        scheduleType?: 'standard' | 'reschedule_only';
      }>
    ) => {
      const modal = templateModal;
      if (!modal) return;
      setTemplateModal(null);
      setShowLoadingModal(true);
      try {
        const psychId = String(psychologist.id);
        const appointmentTypeIds = appointmentTypes.map((apt) => apt.id);
        let localTemplate = [...template];
        let mergedCount = 0;
        let createdCount = 0;

        for (const input of inputs) {
          const weekdayIdx =
            typeof input.weekdayIdx === 'number' ? input.weekdayIdx : modal.weekdayIdx;
          const inputStart = input.startTime.slice(0, 5);
          const inputEnd = input.endTime.slice(0, 5);
          const startTimeWithSeconds =
            input.startTime.length === 5 ? `${input.startTime}:00` : input.startTime;
          const endTimeWithSeconds =
            input.endTime.length === 5 ? `${input.endTime}:00` : input.endTime;
          const startUTCResult = localTimeToUTCWithDayShift(startTimeWithSeconds, selectedTimeZone);
          const startTimeUTC = startUTCResult.time;
          const endTimeUTC = localTimeToUTC(endTimeWithSeconds, selectedTimeZone);
          // Adjust weekday to the UTC day of start_time
          const adjustedWeekdayIdx = ((weekdayIdx + startUTCResult.dayShift) % 7 + 7) % 7;
          const backendWeekday = convertFrontendDayToBackend(adjustedWeekdayIdx);

          const { above, below } = findAdjacentSchedules(
            localTemplate.map((t) => ({ ...t, id: String(t.id) })),
            weekdayIdx,
            inputStart,
            inputEnd,
            input.scheduleType ?? 'standard'
          );

          if (above && below) {
            // Bridge: merge above + new + below into above, delete below
            // Step 1: extend above to cover the gap — creates only the new gap slots via delta
            await updatePsychologistWorkSchedule(String(above.id), {
              end_time: endTimeUTC,
            });
            // Step 2: extend above to cover below's range — skip slot management to avoid duplicates
            const belowEndWithSeconds =
              below.endTime.length === 5 ? `${below.endTime}:00` : below.endTime;
            const belowEndUTC = localTimeToUTC(belowEndWithSeconds, selectedTimeZone);
            await updatePsychologistWorkSchedule(String(above.id), {
              end_time: belowEndUTC,
              skip_slot_management: true,
            });
            // Step 3: soft-delete below's record — preserve its existing slots
            await deletePsychologistWorkSchedule(String(below.id), { preserve_slots: true });
            localTemplate = localTemplate
              .map((t) =>
                String(t.id) === String(above.id) ? { ...t, endTime: below.endTime } : t
              )
              .filter((t) => String(t.id) !== String(below.id));
            mergedCount++;
          } else if (above) {
            // Extend above's end to cover the new range
            await updatePsychologistWorkSchedule(String(above.id), {
              end_time: endTimeUTC,
            });
            localTemplate = localTemplate.map((t) =>
              String(t.id) === String(above.id) ? { ...t, endTime: inputEnd } : t
            );
            mergedCount++;
          } else if (below) {
            // Extend below's start to cover the new range
            await updatePsychologistWorkSchedule(String(below.id), {
              start_time: startTimeUTC,
            });
            localTemplate = localTemplate.map((t) =>
              String(t.id) === String(below.id) ? { ...t, startTime: inputStart } : t
            );
            mergedCount++;
          } else {
            // No adjacency — create normally
            const payload = {
              weekday: backendWeekday,
              start_time: startTimeUTC,
              end_time: endTimeUTC,
              active: true,
              appointment_types: appointmentTypeIds.length > 0 ? appointmentTypeIds : undefined,
              schedule_type: input.scheduleType ?? 'standard',
            };
            const created = await createPsychologistWorkSchedule(payload);
            const createdId = (created as unknown as { id?: string | number } | null)?.id;
            localTemplate.push({
              id:
                typeof createdId === 'string' || typeof createdId === 'number'
                  ? createdId
                  : `${psychId}-${weekdayIdx}-${inputStart}`,
              psychologistId: psychId,
              weekday: weekdayIdx,
              startTime: inputStart,
              endTime: inputEnd,
              active: true,
              scheduleType: input.scheduleType ?? 'standard',
            });
            createdCount++;
          }
        }

        setTemplate(localTemplate);
        setTemplateReady(true);

        invalidateAllCaches();
        await loadData();

        if (mergedCount > 0 && createdCount === 0) {
          showSuccess(
            mergedCount === 1
              ? 'Rango de trabajo fusionado exitosamente'
              : `${mergedCount} rangos de trabajo fusionados exitosamente`
          );
        } else if (mergedCount > 0 && createdCount > 0) {
          showSuccess(
            `${createdCount} rango(s) creado(s) y ${mergedCount} fusionado(s) exitosamente`
          );
        } else {
          showSuccess(
            createdCount === 1
              ? 'Rango de trabajo creado exitosamente'
              : `${createdCount} rangos de trabajo creados exitosamente`
          );
        }
        try {
          window.dispatchEvent(new CustomEvent('psi-work-range-created'));
        } catch {
          /* noop */
        }
      } catch (error) {
        console.error('Error creating schedule:', error);
        showError('Error al crear el rango de trabajo');
      } finally {
        setShowLoadingModal(false);
      }
    },
    [
      templateModal,
      psychologist.id,
      appointmentTypes,
      template,
      selectedTimeZone,
      loadData,
      showSuccess,
      showError,
      setTemplate,
      setTemplateReady,
      invalidateAllCaches,
    ]
  );

  // -------------------------------------------------------------------------
  // handleEditWorkRangeModalDelete — onDelete handler for EditWorkRangeModal
  // -------------------------------------------------------------------------

  const handleEditWorkRangeModalDelete = useCallback(
    async (scheduleId: string | number) => {
      setEditModal(null);

      if (editMode) {
        // In edit mode, dispatch event to update WorkSchedulePanel
        window.dispatchEvent(
          new CustomEvent('psi-delete-work-range-from-edit', { detail: { scheduleId } })
        );
        return;
      }

      // Not in edit mode — perform direct API call
      setShowLoadingModal(true);
      try {
        await deletePsychologistWorkSchedule(String(scheduleId));
        setTemplate((prev) => prev.filter((t) => t.id !== scheduleId));

        invalidateAllCaches();
        await loadData();

        showSuccess('Rango de trabajo eliminado exitosamente');
      } catch (error) {
        console.error('Error deleting schedule:', error);
        showError('Error al eliminar el rango de trabajo');
      } finally {
        setShowLoadingModal(false);
      }
    },
    [editMode, setTemplate, loadData, showSuccess, showError, invalidateAllCaches]
  );

  // -------------------------------------------------------------------------
  // handleEditWorkRangeModalSave — onSave handler for EditWorkRangeModal
  // -------------------------------------------------------------------------

  const handleEditWorkRangeModalSave = useCallback(
    async (input: {
      scheduleId: string | number;
      weekdayIdx: number;
      startTime: string;
      endTime: string;
      scheduleType?: 'standard' | 'reschedule_only';
    }) => {
      setEditModal(null);

      if (editMode) {
        // In edit mode, dispatch event to update WorkSchedulePanel
        const inputStart = input.startTime.slice(0, 5);
        const inputEnd = input.endTime.slice(0, 5);
        window.dispatchEvent(
          new CustomEvent('psi-update-work-range-from-edit', {
            detail: {
              scheduleId: input.scheduleId,
              weekdayIdx: input.weekdayIdx,
              startTime: inputStart,
              endTime: inputEnd,
              scheduleType: input.scheduleType,
            },
          })
        );
        return;
      }

      // Not in edit mode — perform direct API call
      setShowLoadingModal(true);
      try {
        const inputStart = input.startTime.slice(0, 5);
        const inputEnd = input.endTime.slice(0, 5);
        const startTimeWithSeconds =
          input.startTime.length === 5 ? `${input.startTime}:00` : input.startTime;
        const endTimeWithSeconds =
          input.endTime.length === 5 ? `${input.endTime}:00` : input.endTime;

        const startUTCResult = localTimeToUTCWithDayShift(startTimeWithSeconds, selectedTimeZone);
        const startTimeUTC = startUTCResult.time;
        const endTimeUTC = localTimeToUTC(endTimeWithSeconds, selectedTimeZone);

        const adjustedWeekdayIdx = ((input.weekdayIdx + startUTCResult.dayShift) % 7 + 7) % 7;
        const payload = {
          weekday: convertFrontendDayToBackend(adjustedWeekdayIdx),
          start_time: startTimeUTC,
          end_time: endTimeUTC,
          active: true,
          schedule_type: input.scheduleType,
        };
        await updatePsychologistWorkSchedule(String(input.scheduleId), payload);

        // Update local template with the new values
        let localTemplate = template.map((t) =>
          t.id === input.scheduleId
            ? {
                ...t,
                weekday: input.weekdayIdx,
                startTime: inputStart,
                endTime: inputEnd,
                active: true,
                scheduleType: input.scheduleType ?? 'standard',
              }
            : t
        );

        // Check if the edited schedule is now adjacent to others and merge
        const othersOnly = localTemplate.filter((t) => String(t.id) !== String(input.scheduleId));
        const { above, below } = findAdjacentSchedules(
          othersOnly.map((t) => ({ ...t, id: String(t.id) })),
          input.weekdayIdx,
          inputStart,
          inputEnd,
          input.scheduleType
        );

        let merged = false;
        if (above && below) {
          // Edited schedule bridges above and below — absorb into above
          const belowEndWithSeconds =
            below.endTime.length === 5 ? `${below.endTime}:00` : below.endTime;
          const belowEndUTC = localTimeToUTC(belowEndWithSeconds, selectedTimeZone);
          const aboveStartWithSeconds =
            above.startTime.length === 5 ? `${above.startTime}:00` : above.startTime;
          const aboveStartUTC = localTimeToUTC(aboveStartWithSeconds, selectedTimeZone);
          await updatePsychologistWorkSchedule(String(above.id), {
            start_time: aboveStartUTC,
            end_time: belowEndUTC,
            skip_slot_management: true,
          });
          await deletePsychologistWorkSchedule(String(input.scheduleId), { preserve_slots: true });
          await deletePsychologistWorkSchedule(String(below.id), { preserve_slots: true });
          localTemplate = localTemplate
            .map((t) =>
              String(t.id) === String(above.id) ? { ...t, endTime: below.endTime } : t
            )
            .filter(
              (t) =>
                String(t.id) !== String(input.scheduleId) && String(t.id) !== String(below.id)
            );
          merged = true;
        } else if (above) {
          // Merge with above
          await updatePsychologistWorkSchedule(String(above.id), {
            end_time: endTimeUTC,
            skip_slot_management: true,
          });
          await deletePsychologistWorkSchedule(String(input.scheduleId), { preserve_slots: true });
          localTemplate = localTemplate
            .map((t) =>
              String(t.id) === String(above.id) ? { ...t, endTime: inputEnd } : t
            )
            .filter((t) => String(t.id) !== String(input.scheduleId));
          merged = true;
        } else if (below) {
          // Merge with below
          await updatePsychologistWorkSchedule(String(below.id), {
            start_time: startTimeUTC,
            skip_slot_management: true,
          });
          await deletePsychologistWorkSchedule(String(input.scheduleId), { preserve_slots: true });
          localTemplate = localTemplate
            .map((t) =>
              String(t.id) === String(below.id) ? { ...t, startTime: inputStart } : t
            )
            .filter((t) => String(t.id) !== String(input.scheduleId));
          merged = true;
        }

        setTemplate(localTemplate);

        invalidateAllCaches();
        await loadData();

        showSuccess(
          merged ? 'Rango de trabajo fusionado exitosamente' : 'Rango de trabajo actualizado exitosamente'
        );
      } catch (error) {
        console.error('Error updating schedule:', error);
        showError('Error al actualizar el rango de trabajo');
      } finally {
        setShowLoadingModal(false);
      }
    },
    [
      editMode,
      template,
      selectedTimeZone,
      setTemplate,
      loadData,
      showSuccess,
      showError,
      invalidateAllCaches,
    ]
  );

  // -------------------------------------------------------------------------
  // handleEditSpecificDayModalDelete — onDelete handler for editSpecificDayModal
  // -------------------------------------------------------------------------

  const handleEditSpecificDayModalSave = useCallback(
    async (input: {
      scheduleId: string | number;
      weekdayIdx: number;
      startTime: string;
      endTime: string;
      scheduleType?: 'standard' | 'reschedule_only';
    }) => {
      if (!editSpecificDayModal) return;
      const sdDate = editSpecificDayModal.date;
      const [sdY, sdM, sdD] = sdDate.split('-').map(Number);
      const sdJsDay = new Date(sdY, sdM - 1, sdD).getDay();
      const sdWeekdayIdx = (sdJsDay + 6) % 7;
      const slotsForDate = dateSpecificSlots.filter((s) => s.date === sdDate);

      setEditSpecificDayModal(null);
      const updated = slotsForDate.map((s) => {
        if (
          s.startTime === editSpecificDayModal.startTime &&
          s.endTime === editSpecificDayModal.endTime
        ) {
          return { ...s, startTime: input.startTime, endTime: input.endTime, scheduleType: input.scheduleType };
        }
        return s;
      });
      const inputs = updated.map((r) => ({
        startTime: r.startTime,
        endTime: r.endTime,
        weekdayIdx: sdWeekdayIdx,
        date: sdDate,
        scheduleType: (r.scheduleType ?? 'standard') as 'standard' | 'reschedule_only',
      }));
      await handleSpecificDaySave(inputs, sdDate);
    },
    [editSpecificDayModal, dateSpecificSlots, handleSpecificDaySave]
  );

  const handleEditSpecificDayModalDelete = useCallback(async () => {
    if (!editSpecificDayModal) return;
    const sdDate = editSpecificDayModal.date;
    const [sdY, sdM, sdD] = sdDate.split('-').map(Number);
    const sdJsDay = new Date(sdY, sdM - 1, sdD).getDay();
    const sdWeekdayIdx = (sdJsDay + 6) % 7;
    const slotsForDate = dateSpecificSlots.filter((s) => s.date === sdDate);

    setEditSpecificDayModal(null);
    const remaining = slotsForDate.filter(
      (s) =>
        !(
          s.startTime === editSpecificDayModal.startTime &&
          s.endTime === editSpecificDayModal.endTime
        )
    );
    const inputs = remaining.map((r) => ({
      startTime: r.startTime,
      endTime: r.endTime,
      weekdayIdx: sdWeekdayIdx,
      date: sdDate,
      scheduleType: (r.scheduleType ?? 'standard') as 'standard' | 'reschedule_only',
    }));
    await handleSpecificDaySave(inputs, sdDate);
  }, [editSpecificDayModal, dateSpecificSlots, handleSpecificDaySave]);

  // -------------------------------------------------------------------------
  // handleWorkRangeOverlayDelete — onDelete handler for WorkRangeOverlay
  // -------------------------------------------------------------------------

  const handleWorkRangeOverlayDelete = useCallback(
    async (scheduleId: string | number) => {
      setShowLoadingModal(true);
      try {
        await deletePsychologistWorkSchedule(String(scheduleId));
        setTemplate((prev) => prev.filter((t) => t.id !== scheduleId));

        invalidateAllCaches();
        await loadData();

        showSuccess('Rango de trabajo eliminado exitosamente');
      } catch (error) {
        console.error('Error deleting schedule:', error);
        showError('Error al eliminar el rango de trabajo');
      } finally {
        setShowLoadingModal(false);
      }
    },
    [setTemplate, loadData, showSuccess, showError, invalidateAllCaches]
  );

  // -------------------------------------------------------------------------
  // Expose refs used by the parent for onHasChangesChange callbacks
  // (the parent reads .current directly; callbacks are wired externally)
  // -------------------------------------------------------------------------
  void onSpecificDayHasChangesChange; // consumed by parent via specificDayHasChangesRef.current
  void onBlockHasChangesChange; // consumed by parent via blockHasChangesRef.current

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // State
    templateModal,
    setTemplateModal,
    editModal,
    setEditModal,
    editSpecificDayModal,
    setEditSpecificDayModal,
    specificDayPreview,
    setSpecificDayPreview,
    specificDaySelectedDate,
    setSpecificDaySelectedDate,
    showLoadingModal,
    setShowLoadingModal,
    selectedRangeId,
    setSelectedRangeId,
    // Refs
    workScheduleSavedRef,
    specificDayHasChangesRef,
    blockHasChangesRef,
    // Computed
    openCreateWeekdayIdx,
    // Handlers
    handleSetTemplateModal,
    handlePanelSave,
    handleSpecificDaySave,
    handleCreateWorkRangeModalSave,
    handleEditWorkRangeModalDelete,
    handleEditWorkRangeModalSave,
    handleEditSpecificDayModalDelete,
    handleEditSpecificDayModalSave,
    handleWorkRangeOverlayDelete,
  };
}
